// PHILTECH — Maya "Merchant Processing" fee sync
// v2.1 (Oct 2026): fixes Firefox reload loop / radio flipping back on the cart page
//   - page load trusts the saved cart attribute, never the radio (Firefox restores stale radio state)
//   - radios are forced to match the saved choice on load and on back/forward (bfcache)
//   - reloads use a fresh navigation instead of location.reload() (no form-state restore)
//   - hard stop: at most 3 automatic reloads per 10 seconds
(() => {
  const CFG = window.MAYA_FEE || {};
  const VARIANT_ID = Number(CFG.variantId);
  const RATE = Number(CFG.rate);
  if (!VARIANT_ID || !RATE) return;

  const onCartPage = () => /\/cart\/?$/.test(location.pathname);
  const savedChoice = cart => (cart.attributes.payment_choice === 'Maya' ? 'Maya' : 'Other');

  // ---- reload helper with loop protection ----
  function safeReload() {
    const KEY = 'philtech_maya_reloads';
    let log = [];
    try { log = JSON.parse(sessionStorage.getItem(KEY) || '[]'); } catch (_) {}
    const now = Date.now();
    log = log.filter(t => now - t < 10000);
    if (log.length >= 3) return; // something is fighting us — stop instead of looping
    log.push(now);
    try { sessionStorage.setItem(KEY, JSON.stringify(log)); } catch (_) {}
    // Fresh navigation (not location.reload) so Firefox doesn't restore old radio state
    location.replace(location.pathname + location.search);
  }

  // ---- make the radios show what the cart actually has saved ----
  function setRadios(choice) {
    document.querySelectorAll('input[name="maya_fee_choice"]').forEach(r => {
      r.setAttribute('autocomplete', 'off');
      r.checked = r.value === choice;
    });
  }

  let busy = false;

  // choice: 'Maya' | 'Other' | undefined (undefined = keep the saved choice, just fix the fee)
  async function syncMayaFee({ choice, reload = false } = {}) {
    if (busy) return false;
    busy = true;
    try {
      const cart = await (await fetch('/cart.js', { cache: 'no-store' })).json();
      const want = choice || savedChoice(cart);

      const fee = cart.items.find(i => i.variant_id === VARIANT_ID);
      const feeQty = fee ? fee.quantity : 0;
      const goods = (cart.items_subtotal_price - (fee ? fee.final_line_price : 0)) / 100;
      const qty = want === 'Maya' && goods > 0 ? Math.ceil(goods * RATE) : 0;

      if (feeQty === qty && savedChoice(cart) === want) return false;

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [VARIANT_ID]: qty }, attributes: { payment_choice: want } })
      });
      if (reload) safeReload();
      return true;
    } finally {
      busy = false;
    }
  }

  // ---- checkout guard: go to /cart first; on /cart, sync the fee then continue ----
  async function guardCheckout(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    if (!onCartPage()) { location.href = '/cart'; return; }
    await syncMayaFee();
    location.href = '/checkout';
  }

  document.addEventListener('click', e => {
    if (e.target.closest('[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]')) guardCheckout(e);
  }, true);

  document.addEventListener('submit', e => {
    const s = e.submitter;
    if ((s && s.name === 'checkout') || /\/checkout/.test(e.target.getAttribute('action') || '')) guardCheckout(e);
  }, true);

  // ---- the ONLY place the radio is read: an actual customer click ----
  document.addEventListener('change', e => {
    if (e.target.name !== 'maya_fee_choice') return;
    document.querySelectorAll('input[name="maya_fee_choice"]').forEach(r => (r.disabled = true));
    syncMayaFee({ choice: e.target.value === 'Maya' ? 'Maya' : 'Other', reload: true })
      .then(changed => { if (!changed) document.querySelectorAll('input[name="maya_fee_choice"]').forEach(r => (r.disabled = false)); });
  });

  // ---- page load / back-forward: show the saved choice, then make sure the fee matches it ----
  async function onShow() {
    try {
      const cart = await (await fetch('/cart.js', { cache: 'no-store' })).json();
      setRadios(savedChoice(cart));
    } catch (_) {}
    syncMayaFee({ reload: onCartPage() });
  }
  document.addEventListener('DOMContentLoaded', onShow);
  window.addEventListener('pageshow', e => { if (e.persisted) onShow(); });

  // ---- any cart change (quantity edits, add to cart): recalc fee for the saved choice ----
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => syncMayaFee({ reload: onCartPage() }));
  }
})();